import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";

export interface ProcessStep {
  key: string;
  label: string;
}

interface Props {
  steps: ProcessStep[];
  currentKey: string;
  onStepClick?: (key: string) => void;
  /** Max width of the whole bar. Default suits a few steps; pass "full" for many. */
  maxW?: string;
}

/**
 * Horizontal numbered process bar matching the v2 design prototype (WorkflowStepper):
 * numbered circles joined by connector lines, labels beneath, teal for done/active and
 * muted for upcoming. Steps are clickable when `onStepClick` is provided.
 */
export const ProcessStepBar = ({ steps, currentKey, onStepClick, maxW = "800px" }: Props) => {
  const currentIndex = steps.findIndex((s) => s.key === currentKey);
  const clickable = !!onStepClick;

  return (
    <Flex align="flex-start" w="full" maxW={maxW} mx="auto">
      {steps.map((step, i) => {
        const completed = i < currentIndex;
        const active = i === currentIndex;
        const reached = completed || active;
        const isLast = i === steps.length - 1;

        return (
          <Flex key={step.key} align="flex-start" flex={isLast ? "none" : "1"}>
            <Flex
              direction="column"
              align="center"
              gap="1.5"
              w="84px"
              cursor={clickable ? "pointer" : "default"}
              onClick={clickable ? () => onStepClick?.(step.key) : undefined}
              role={clickable ? "button" : undefined}
              aria-current={active ? "step" : undefined}
            >
              <Flex
                align="center"
                justify="center"
                w="8"
                h="8"
                rounded="full"
                flexShrink={0}
                bg={reached ? "smPrimary" : "smBackgroundSecondary"}
                color={reached ? "white" : "smTextUpcoming"}
                transition="all 0.2s ease"
              >
                {completed ? (
                  <Icon as={FiCheck} boxSize="4" />
                ) : (
                  <Text fontSize="13px" fontWeight="semibold">
                    {i + 1}
                  </Text>
                )}
              </Flex>
              <Text
                fontSize="11px"
                fontWeight={active ? "semibold" : "medium"}
                color={reached ? "smPrimary" : "smTextUpcoming"}
                textAlign="center"
                lineHeight="1.2"
              >
                {step.label}
              </Text>
            </Flex>
            {!isLast && <Box flex="1" mt="4" h="2px" bg={completed ? "smPrimary" : "smBorder"} />}
          </Flex>
        );
      })}
    </Flex>
  );
};
